import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { faFlask } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { DeleteRowModal } from "../../shell/DeleteRowModal";
import { focusMatches } from "../../shell/focusTarget";
import { IssueBar } from "../../shell/IssueBar";
import { ScoreLeaveGuard } from "../../shell/ScoreLeaveGuard";
import { ScoreSaveBar } from "../../shell/ScoreSaveBar";
import { ScoreShell } from "../../shell/ScoreShell";
import { ChipStatus } from "../../shell/StatusChip";
import { PersonaView, SegmentGroup, SegmentView } from "../../shell/scoreConfigView";
import { computeScoreIssues, issuesFor, issuesForStep, ScoreIssue } from "../../shell/scoreIssues";
import { scoreStepPath } from "../../shell/scoreSteps";
import { saveBlockerText } from "../../shell/saveBlocker";
import { useConfigDraft } from "../../shell/useConfigDraft";
import { useSaveBlockerFocus } from "../../shell/useSaveBlockerFocus";
import { useFocusTarget } from "../../shell/useFocusTarget";
import { useScoreConfig } from "../../shell/useScoreConfig";
import { createScoringHref, editScoringHref } from "../ScoreOverView/ScoreOverview";

import { SegmentDetail } from "./SegmentDetail";
import { SegmentGroupBox } from "./SegmentGroupBox";
import { SegmentPreview } from "./SegmentPreview";
import { SegmentTestModal } from "./SegmentTestModal";

const HELP_URL = "https://help.signalist.io/segments";

const GROUPS: SegmentGroup[] = ["PROSPECT", "PARTNER", "INVESTOR"];

/**
 * Company Segments (PREC-4055): the companies the user sells to, sells with,
 * and the investor types, each as a chip row with one detail card open at a
 * time. Every edit lands in the shared draft and saves in one go.
 */
export const ScoreCompanyCategory: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { score, criteria, refetch } = useScoreConfig();
	const { target, clear: clearFocus } = useFocusTarget();
	const edits = useConfigDraft(score, criteria, refetch, {
		saveError: t("leadScoring.saveError"),
		blocked: (blocker) => saveBlockerText(blocker, t),
	});

	// null = nothing chosen yet (the first prospect segment opens); "" = explicitly collapsed.
	const [selectedKey, setSelectedKey] = useState<string | null>(null);
	const [deleting, setDeleting] = useState<SegmentView | null>(null);
	const [testing, setTesting] = useState(false);

	const issues = useMemo(
		() => issuesForStep(computeScoreIssues({ score: edits.draft, criteria }), "company"),
		[edits.draft, criteria]
	);

	// A refused save selects and flashes the row it names (PREC-4987).
	useSaveBlockerFocus(edits.blocker);

	// A ?focus= deep link or an issue click selects the segment; ?add=segment adds one.
	useEffect(() => {
		if (target?.kind === "segment") {
			setSelectedKey(target.id);
		}
	}, [target]);

	const draftReady = !!edits.draft;
	const firstProspectKey = edits.segments.find((row) => row.group === "PROSPECT")?.key ?? null;
	const openKey = selectedKey === null ? firstProspectKey : selectedKey || null;

	useEffect(() => {
		if (searchParams.get("add") === "segment" && draftReady) {
			setSelectedKey(`${edits.addSegment("PROSPECT")}`);
			setSearchParams(
				(current) => {
					const next = new URLSearchParams(current);

					next.delete("add");

					return next;
				},
				{ replace: true }
			);
		}
		// Runs once the draft exists; the param is consumed on that run.
	}, [searchParams, draftReady]);

	const statusOf = (segment: SegmentView): ChipStatus =>
		!segment.active ? "off" : issuesFor(issues, "segment", segment.key).length > 0 ? "bad" : "ok";

	const previewOf = (segment: SegmentView) => (
		<SegmentPreview
			criteria={criteria}
			issues={issuesFor(issues, "segment", segment.key)}
			rows={edits.personas
				.filter((persona) => persona.active && edits.isLinked(segment, persona))
				.map((persona) => ({ persona, assignment: edits.assignmentOf(segment, persona) }))}
			segment={segment}
		/>
	);

	const selectIssue = (issue: ScoreIssue) => setSelectedKey(issue.id);

	const cellActions = {
		onPick: edits.pick,
		onClear: edits.clear,
		onDeactivate: (segment: SegmentView, persona: PersonaView) =>
			edits.setLinked(segment, persona, false),
		onCreate: (segment: SegmentView, persona: PersonaView) =>
			navigate(createScoringHref(segment, persona)),
		onEdit: (criteriaId: number) => navigate(editScoringHref(criteriaId)),
	};

	const groupEnabled = (group: SegmentGroup): boolean =>
		group === "PROSPECT"
			? true
			: group === "PARTNER"
				? !!edits.draft?.partners
				: !!edits.draft?.investors;

	const detailFor = (group: SegmentGroup) => {
		const segment = edits.segments.find((row) => row.key === openKey && row.group === group);

		return (
			segment && (
				<SegmentDetail
					criteria={criteria}
					edits={edits}
					flash={focusMatches(target, "segment", segment.key)}
					segment={segment}
					onCreatePersona={() => navigate(`${scoreStepPath("prospect")}?add=persona`)}
					onDelete={setDeleting}
					onFlashDone={clearFocus}
					{...cellActions}
				/>
			)
		);
	};

	const runTest = async () => {
		if (!edits.dirty || (await edits.save())) {
			setTesting(true);
		}
	};

	return (
		<ScoreShell
			helpUrl={HELP_URL}
			intro={t("leadScoring.segments.intro")}
			step="company"
			introActions={
				<button
					className="flex h-7 cursor-pointer items-center gap-1.5 rounded-lg border border-border-light bg-white px-2.5 text-xs font-medium text-text hover:bg-background-light"
					data-testid="segments-test"
					type="button"
					onClick={runTest}
				>
					<Icon className="h-3 w-3" icon={faFlask} />
					{t("leadScoring.segments.test")}
				</button>
			}
		>
			<IssueBar
				issues={issues}
				subject={t("leadScoring.segments.subject")}
				onSelect={selectIssue}
			/>

			{GROUPS.map((group) => (
				<SegmentGroupBox
					key={group}
					enabled={groupEnabled(group)}
					group={group}
					previewOf={previewOf}
					segments={edits.segments.filter((segment) => segment.group === group)}
					selectedKey={openKey}
					statusOf={statusOf}
					onAdd={
						group === "INVESTOR" ? undefined : () => setSelectedKey(`${edits.addSegment(group)}`)
					}
					onSelect={(key) => setSelectedKey(key ?? "")}
					onToggleEnabled={
						group === "PARTNER"
							? edits.setPartners
							: group === "INVESTOR"
								? edits.setInvestors
								: undefined
					}
				>
					{detailFor(group)}
				</SegmentGroupBox>
			))}

			{deleting && (
				<DeleteRowModal
					body={t("leadScoring.segments.deleteBody", {
						personas: edits.personas.filter((persona) => edits.isLinked(deleting, persona)).length,
					})}
					title={t("leadScoring.segments.deleteTitle", {
						name: deleting.name || t("leadScoring.shell.untitled"),
					})}
					onClose={() => setDeleting(null)}
					onDeactivate={() => {
						edits.updateSegment(deleting.id as number, { active: false });
						setDeleting(null);
					}}
					onDelete={() => {
						edits.removeSegment(deleting.id as number);
						setSelectedKey(null);
						setDeleting(null);
					}}
				/>
			)}

			{testing && (
				<SegmentTestModal
					onApplySuggestion={(suggestion) => {
						setSelectedKey(
							`${edits.addSegment("PROSPECT", { category: suggestion.name, description: suggestion.description })}`
						);
						setTesting(false);
					}}
					onClose={() => setTesting(false)}
				/>
			)}

			<ScoreSaveBar
				dirty={edits.dirty}
				error={edits.error}
				saving={edits.saving}
				onDiscard={edits.discard}
				onSave={edits.save}
			/>
			<ScoreLeaveGuard when={edits.dirty} onDiscard={edits.discard} onSave={edits.save} />
		</ScoreShell>
	);
};
